import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import copy from 'clipboard-copy';
import { useEffect, useState } from "react";

import numeral from "numeral";
import {
    useMap,
} from 'react-leaflet';

import { IconButton } from "@mui/material";
import classNames from 'classnames';
import DmsCoordinates from "dms-conversion";
import { Bounds } from '../core/global';
import { getBboxAreaKm2 } from '../helpers/core';
import styles from "../styles/components/mouse-coordinates.module.scss";

interface LatLonBannerProps {
  lat: string | number | null | undefined
  lon: string | number | null | undefined
  className?: string
  title?: string
  degrees?: boolean
  enableCopy?: boolean
}

export const LatLonBanner = ({
  lat,
  lon,
  className,
  title,
  degrees = false,
  enableCopy = false,
}: LatLonBannerProps) => {

  if (lat === null || typeof lat === 'undefined' || lon === null || typeof lon === 'undefined') {
    return null
  }

  if (degrees) {
    const dmsCoords = new DmsCoordinates(Number(lat), Number(lon));
    const { longitude, latitude } = dmsCoords.dmsArrays;
    const [lat_d, lat_m, lat_s , lat_cardinal] = latitude;
    const [lon_d, lon_m, lon_s, lon_cardinal] = longitude;
    return (
      <div className={classNames(styles.wrapper, className)}>
        <span>
          {title && <h5>{title}</h5>}
          <span><strong>{lat_d}°{lat_m}′ {lat_cardinal}</strong></span>
          <span><strong>{lon_d}°{lon_m}′ {lon_cardinal}</strong></span>
        </span>
        {
        enableCopy && 
          <IconButton
            size='small'
            onClick={() => {
              const str = getStrToCopy(lat, lon, true)
              copy(str)
            }
          }>
            <ContentCopyIcon />
          </IconButton>
        }
      </div>
    )
  }

  return (
    <div className={classNames(styles.wrapper, className, {
      [styles.hasButton]: enableCopy
    })}>
      <span>
        {title && <h5>{title}</h5>}
        <span><strong>Lat:</strong> {numeral(lat).format("0,0.000")}</span>
        <span><strong>Lon:</strong> {numeral(lon).format("0,0.000")}</span>
      </span>
      {
        enableCopy && 
        <IconButton
          size='small'
          onClick={() => {
            const str = getStrToCopy(lat, lon, false)
            copy(str)
          }
        }>
          <ContentCopyIcon />
        </IconButton>
      }
    </div>
  )
}


const LatLonElement = ({lat, lon, degrees = false} :{
  lat: string | number | null | undefined, 
  lon: string | number | null | undefined
  degrees?: boolean
}) => {

  if (lat === null || typeof lat === 'undefined' || lon === null || typeof lon === 'undefined') {
    return null
  }

  const useLat = typeof lat === "string" ? Number(lat) : lat
  const useLon = typeof lon === "string" ? Number(lon) : lon

  if (isNaN(useLat) || isNaN(useLon)) {
    return null
  }

  if (degrees) {
    const dmsCoords = new DmsCoordinates(useLat, useLon);
    const { longitude, latitude } = dmsCoords.dmsArrays;
    const [lat_d, lat_m, lat_s , lat_cardinal] = latitude;
    const [lon_d, lon_m, lon_s, lon_cardinal] = longitude;
  
    return (
      <>
        <span><strong>{lat_d}°{lat_m}′ {lat_cardinal}</strong></span>
        <span><strong>{lon_d}°{lon_m}′ {lon_cardinal}</strong></span>
      </>
    )  
  }

  return (
    <>
      <span><strong>Lat:</strong> {numeral(useLat).format("0,0.000")}</span>
      <span><strong>Lon:</strong> {numeral(useLon).format("0,0.000")}</span>
    </>
  )
}

export const getStrToCopy = (lat: string | number, lon: string | number, degrees = false) => {
  if (degrees) {
    const dmsCoords = new DmsCoordinates(Number(lat), Number(lon));
    const { longitude, latitude } = dmsCoords.dmsArrays;
    const [lat_d, lat_m, lat_s , lat_cardinal] = latitude;
    const [lon_d, lon_m, lon_s, lon_cardinal] = longitude;
    return `${lat_d}°${lat_m}′ ${lat_cardinal} ${lon_d}°${lon_m}′ ${lon_cardinal}`
  }

  return `${numeral(lat).format("0,0.00000")}, ${numeral(lon).format("0,0.00000")}`
}

interface BoundsBannerProps {
  bounds: Bounds | null
  className?: string
  title?: string
  degrees?: boolean
  enableCopy?: boolean
}


export const BoundsBanner = ({
  bounds,
  className,
  title,
  degrees = false,
  enableCopy = false,
}: BoundsBannerProps) => {

  if (!bounds || bounds === null || (Array.isArray(bounds) && bounds.length !== 4)) {
    return null
  }

  const {
    min: {
      lat: lat_min,
      lng: lon_min,
    }, 
    max: {
      lat: lat_max,
      lng: lon_max,
    }
  } = bounds

  if (degrees) {
    return (
      <div className={classNames(styles.wrapper, className)}>
        <span>
          {title && <h5>{title}</h5>}
          <LatLonElement lat={lat_min} lon={lon_min} degrees />
          <LatLonElement lat={lat_max} lon={lon_max} degrees />
        </span>
        {
        enableCopy && 
          <IconButton
            size='small'
            onClick={() => {
              const str = getStrToCopy(lat_min, lon_min, true) + ' - ' + getStrToCopy(lat_max, lon_max, true)
              copy(str)
            }
          }>
            <ContentCopyIcon />
          </IconButton>
        }
      </div>
    )
  }

  return (
    <>
      <div className={classNames(styles.wrapper, className, {
        [styles.hasButton]: enableCopy
      })}>
        <span>
          {title && <h5>{title}</h5>}
          <LatLonElement lat={lat_min} lon={lon_min} />
          <LatLonElement lat={lat_max} lon={lon_max} />
        </span>
        {
          enableCopy && 
          <IconButton
            size='small'
            onClick={() => {
              const str = getStrToCopy(lat_min, lon_min, false) + ' - ' + getStrToCopy(lat_max, lon_max, false)
              copy(str)
            }
          }>
            <ContentCopyIcon />
          </IconButton>
        }
      </div>
    </>
  )
}

interface BoundsBannerProps {
  bounds: Bounds | null
  className?: string
  title?: string
  degrees?: boolean
  enableCopy?: boolean
  showArea?: boolean
}


export const AreaBanner = ({
  bounds,
  className,
  enableCopy = false,
  title,
}: BoundsBannerProps) => {

  if (!bounds || bounds === null || (Array.isArray(bounds) && bounds.length !== 4)) {
    return null
  }

  const {
    min: {
      lat: lat_min,
      lng: lon_min,
    }, 
    max: {
      lat: lat_max,
      lng: lon_max,
    }
  } = bounds

  const size = numeral(getBboxAreaKm2(bounds)).format("0,0.00")

  return (
    <div className={classNames(styles.wrapper, className)}>
      <span>
        {title && <h5>{title}</h5>}
        <span>{size} km²</span>
      </span>
      {
        enableCopy && 
        <IconButton
          size='small'
          onClick={() => {
            const str = `${size} km²`
            copy(str)
          }
        }>
          <ContentCopyIcon />
        </IconButton>
      }
    </div>
  )
}

interface MouseCoordinatesProps {
  feature?: GeoJSON.Feature
  className?: string
}
export const MouseCoordinates = ({
  className = '',
}: MouseCoordinatesProps) => {    
  const [coords, setCoords] = useState<any>(null);
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    map.addEventListener("mousemove", (e) => {
      setCoords({ lat: e.latlng.lat, lon: e.latlng.lng });
    });
  }, [map]);

  return (
    <>
     {coords && <LatLonBanner className={classNames(styles.innerMap, className)} lat={coords?.lat} lon={coords?.lon} />}
    </>
  )
}